import React, {
  useRef,
  useState,
  useEffect
} from 'react';

import {
  Box,
  Flex,
  Modal,
  Table,
  Button,
  TableRow,
  Paragraph,
  TableBody,
  TableCell,
  TableHead,
  Pagination,
  TableActions,
  useNotification,
  LoadingContainer,
  OrderableTableRow,
  usePaginationReducer,
  useOrderableTableReducer
} from '@sopranodesign/pulse-components';
import {
  useAuth,
  PermissionFilter
} from '@sopranodesign/pulse-auth';
import {
  useQuery,
  useQueryClient
} from '@sopranodesign/pulse-hooks';

import {
  createSitemap,
  deleteSitemap,
  deleteSitemapUrl,
  recrawlSitemapUrl,
  saveSitemapUrlTags,
  createSitemapUrlTag,
  fetchSitemapUrlTags,
  fetchSitemapUrlAvailableTags
} from '../api';
import { SitemapModal, SitemapUrlExplore } from '.';
import WarningMessageBody from './WarningMessageBody';
import { useTagManager } from './useTagManager';
import TagModal from './TagModal';

const Sitemap = () => {
  const { hasPermission } = useAuth();
  const canViewTag = hasPermission('view generative ai tag');

  const { showSuccess, showError } = useNotification();
  const queryClient = useQueryClient();

  const [isModalVisible, setIsModalVisible] = useState(false);
  const [sitemapUrl, setSitemapUrl] = useState('');
  const [selectedOption, setSelectedOption] = useState(null);
  const [selectedSitemap, setSelectedSitemap] = useState(null);
  const [shouldRefetch, setShouldRefetch] = useState(false);
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [modal, setModal] = useState(null);
  const [loading, setLoading] = useState(false);
  const [isTagsModalVisible, setIsTagsModalVisible] = useState(false);
  const [selectedTagsId, setSelectedTagsId] = useState(null);
  const [loadingIds, setLoadingIds] = useState({});
  const [errorIds, setErrorIds] = useState({});

  const isMounted = useRef(true);

  const order = useOrderableTableReducer({ id: 'sitemaps' });
  const pagination = usePaginationReducer({ id: 'sitemaps' });

  const options = [
    { value: 'url', label: 'Single URL' },
    { value: 'xml', label: 'XML Sitemap' },
    { value: 'html', label: 'HTML Sitemap' }
  ];

  const [isUrlPanelOpen, setIsUrlPanelOpen] = useState(false);

  const { data: availableTagsData } = useQuery({
    queryKey: ['generative-ai/sitemap/groups/available'],
    queryFn: fetchSitemapUrlAvailableTags,
    staleTime: 5 * 60 * 1000,
    enabled: canViewTag
  });

  const { tags, selectedTags, setSelectedTags, handleTagChange, setTags } = useTagManager({
    scope: 'generative_ai_sitemap_urls',
    createTagFn: createSitemapUrlTag
  });

  useEffect(() => {
    return () => {
      isMounted.current = false;
    };
  }, []);

  const {
    data: sitemaps,
    isLoading,
    error
  } = useQuery({
    queryKey: ['generative-ai/sitemap', { ...pagination.params, ...order.params }],
    refetchInterval: !isUrlPanelOpen && shouldRefetch ? 5000 : false
  });

  useEffect(() => {
    if (!isMounted.current) {
      return;
    }

    const shouldEnableRefetch = Boolean(sitemaps?.has_pending && sitemaps?.data);
    setShouldRefetch(shouldEnableRefetch);
  }, [sitemaps?.data, sitemaps?.has_pending]);

  const handleConfirm = async (selectedTagsParam = []) => {
    try {
      setIsSubmitting(true);
      const rawUrls = sitemapUrl.split(',').map(url => url.trim()).filter(Boolean);
      const normalizedUrls = rawUrls.map(url => url.toLowerCase().replace(/\/$/, ''));
      const existingUrls =
        sitemaps?.data?.map(s => s.url.toLowerCase().trim().replace(/\/$/, '')) || [];
      const duplicates = normalizedUrls.filter(url => existingUrls.includes(url));

      if (duplicates.length > 0) {
        setIsSubmitting(false);
        showError(
          `The following URLs are already part of your generative AI knowledge: ${duplicates.join(
            ', '
          )}. To replace them, delete the originals first.`
        );
        return;
      }

      const payloads = rawUrls.map(url => {
        const payload = { url, type: selectedOption?.value };
        if (selectedTagsParam && selectedTagsParam.length > 0) {
          payload.groupIds = selectedTagsParam;
        }
        return payload;
      });

      await createSitemap(payloads);
      if (isMounted.current) {
        setIsSubmitting(false);
        showSuccess(`${payloads.length} sitemap${payloads.length > 1 ? 's' : ''} added successfully!`);
        queryClient.invalidateQueries({ queryKey: ['generative-ai/sitemap'] });
        setIsModalVisible(false);
      }
    } catch (err) {
      if (isMounted.current) {
        showError(`Failed to create sitemap(s): ${err?.message || 'Unknown error'}`);
      }
    }
  };

  const modalConfigs = {
    deleteSitemap: (sitemap) => ({
      header: 'Confirm Delete',
      body: (
        <WarningMessageBody
          contentHeader={`Are you sure you want to delete the sitemap "${sitemap.url}"?`}
          contentBody="All associated URLs will be permanently removed."
          warningText="This action cannot be undone."
        />
      ),
      confirmText: 'Delete',
      handleConfirm: async () => {
        setLoading(true);
        try {
          await deleteSitemap(sitemap.id);
          showSuccess(`Sitemap ${sitemap.url} delete requested.`);
          queryClient.invalidateQueries({ queryKey: ['generative-ai/sitemap'] });
        } catch (err) {
          showError(`Failed to delete sitemap: ${err?.message || 'Unknown error'}`);
        } finally {
          setLoading(false);
          setModal(null);
        }
      }
    }),

    deleteUrl: (url) => ({
      header: 'Confirm Delete',
      body: (
        <WarningMessageBody
          contentHeader={`Are you sure you want to delete the sitemap URL "${url.url}"?`}
          contentBody="All synced data will be permanently removed."
          warningText="This action cannot be undone."
        />
      ),
      confirmText: 'Delete',
      handleConfirm: async () => {
        setLoading(true);
        setLoadingIds(prev => ({ ...prev, [url.id]: true }));
        try {
          await deleteSitemapUrl(url.id);
          queryClient.invalidateQueries({
            queryKey: [`generative-ai/sitemap/${selectedSitemap?.id}/urls`]
          });
          showSuccess(`Sitemap URL delete requested`);
        } catch (err) {
          const errorMessage =
            err?.response?.data?.message || err?.message || 'Failed to delete the sitemap URL.';
          setErrorIds(prev => ({ ...prev, [url.id]: errorMessage }));
          showError(errorMessage);
        } finally {
          setLoadingIds(prev => ({ ...prev, [url.id]: false }));
          setLoading(false);
          setModal(null);
        }
      }
    }),

    recrawlUrl: (url) => ({
      header: 'Confirm Recrawl',
      body: (
        <WarningMessageBody
          contentHeader={`Are you sure you want to recrawl the sitemap URL "${url.url}"?`}
        />
      ),
      confirmText: 'Recrawl',
      handleConfirm: async () => {
        setLoading(true);
        setLoadingIds(prev => ({ ...prev, [url.id]: true }));
        try {
          await recrawlSitemapUrl(url.id);
          queryClient.invalidateQueries({
            queryKey: [`generative-ai/sitemap/${selectedSitemap?.id}/urls`]
          });
          showSuccess(`Sitemap recrawl requested`);
        } catch (err) {
          const errorMessage =
            err?.response?.data?.message || err?.message || 'Failed to recrawl the sitemap URL.';
          setErrorIds(prev => ({ ...prev, [url.id]: errorMessage }));
          showError(errorMessage);
        } finally {
          setLoadingIds(prev => ({ ...prev, [url.id]: false }));
          setLoading(false);
          setModal(null);
        }
      }
    })
  };

  const openDeleteUrlModal = (url) => setModal(modalConfigs.deleteUrl(url));
  const openRecrawlModal = (url) => setModal(modalConfigs.recrawlUrl(url));

  const openTagsModal = async (url) => {
    try {
      const fetchedTags = availableTagsData?.data?.data ?? [];
      setTags(fetchedTags);
      const assignedTagsResponse = await fetchSitemapUrlTags(url.id);
      const currentTags = assignedTagsResponse.data.data ?? [];
      setSelectedTags(currentTags.map(tag => tag.id));
      setSelectedTagsId(url.id);
      setIsTagsModalVisible(true);
    } catch (error) {
      showError(error?.message || 'Failed to load tags.');
    }
  };

  const closeViewSitemapUrl = () => {
    setSelectedSitemap(null);
    setIsUrlPanelOpen(false);
    setShouldRefetch(true);
    queryClient.invalidateQueries({ queryKey: ['generative-ai/sitemap'] });
  };

  if (isLoading) {
    return (
      <Flex col center middle tall>
        <LoadingContainer />
        <Paragraph mt>Loading sitemap...</Paragraph>
      </Flex>
    );
  }

  if (error) {
    return <div>Error loading sitemaps: {error.message}</div>;
  }

  const meta = sitemaps?.meta ?? {};
  const hasAnyPending = sitemaps?.has_pending;

  return (
    <Flex col>
      <PermissionFilter can="view generative ai">
        {sitemaps.data?.length === 0 && (
          <Box border="dashed">
            <Flex col middle center>
              <Paragraph mb secondary>No web content has been added yet:</Paragraph>
              <PermissionFilter can="edit generative ai resource">
                <Button
                  icon="plus"
                  onClick={() => setIsModalVisible(true)}
                >
                  Add URLs
                </Button>
              </PermissionFilter>
              {isModalVisible && (
                <SitemapModal
                  handleClose={() => setIsModalVisible(false)}
                  handleSave={handleConfirm}
                  sitemapUrl={sitemapUrl}
                  setSitemapUrl={setSitemapUrl}
                  selectedOption={selectedOption}
                  setSelectedOption={setSelectedOption}
                  options={options}
                  isSubmitting={isSubmitting}
                />
              )}
            </Flex>
          </Box>
        )}

        {sitemaps.data?.length > 0 && (
          <>
            <Flex border xScroll>
              <Table>
                <TableHead>
                  <OrderableTableRow
                    rows={[
                      { label: 'Url', sort: 'url' },
                      { label: 'Type', sort: 'type' },
                      { label: 'Crawled' },
                      null
                    ]}
                    {...order.props}
                  />
                </TableHead>
                <TableBody>
                  {sitemaps.data.map((sitemap, key) => (
                    <TableRow key={key}>
                      <TableCell>{sitemap.url}</TableCell>
                      <TableCell>{sitemap.type}</TableCell>
                      <TableCell>{sitemap.success_count}</TableCell>
                      <TableActions>
                        <Button
                          variant="secondary"
                          icon="eye"
                          colour="primary"
                          onClick={() => {
                            setSelectedSitemap(sitemap);
                            setIsUrlPanelOpen(true);
                          }}
                        >
                          View
                        </Button>

                        <PermissionFilter can="edit generative ai resource">
                          <Button
                            variant="secondary"
                            icon="trash"
                            colour="danger"
                            onClick={() => setModal(modalConfigs.deleteSitemap(sitemap))}
                            disabled={hasAnyPending}
                          >
                            Delete
                          </Button>
                        </PermissionFilter>
                      </TableActions>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            </Flex>

            {meta && <Pagination pagination={meta} {...pagination.props} />}

            <PermissionFilter can="edit generative ai resource">
              <Button
                mt
                mlAuto
                icon="plus"
                onClick={() => setIsModalVisible(true)}
                disabled={hasAnyPending}
              >
                Add URLs
              </Button>
            </PermissionFilter>

            {isModalVisible && (
              <SitemapModal
                handleClose={() => setIsModalVisible(false)}
                handleSave={handleConfirm}
                sitemapUrl={sitemapUrl}
                setSitemapUrl={setSitemapUrl}
                selectedOption={selectedOption}
                setSelectedOption={setSelectedOption}
                options={options}
                isSubmitting={isSubmitting}
              />
            )}

            <SitemapUrlExplore
              sitemap={selectedSitemap}
              handleHide={closeViewSitemapUrl}
              onOpenDeleteModal={openDeleteUrlModal}
              onOpenRecrawlModal={openRecrawlModal}
              onOpenTagsModal={openTagsModal}
              loadingIds={loadingIds}
              errorIds={errorIds}
            />

            <TagModal
              isVisible={isTagsModalVisible}
              header="Sitemap URL Tags"
              onCancel={() => {
                setIsTagsModalVisible(false);
                setSelectedTags([]);
              }}
              onConfirm={async () => {
                try {
                  await saveSitemapUrlTags({
                    sitemapId: selectedTagsId,
                    groupIds: selectedTags
                  });
                  queryClient.invalidateQueries({
                    queryKey: [`generative-ai/sitemap/${selectedSitemap?.id}/urls`]
                  });
                  setIsTagsModalVisible(false);
                  setSelectedTags([]);
                  showSuccess('Tags assigned successfully!');
                } catch (error) {
                  showError(error?.message || 'Failed to assign tags.');
                }
              }}
              tags={tags}
              selectedTags={selectedTags}
              handleTagChange={handleTagChange}
            />

            {modal && (
              <Modal
                header={modal.header}
                body={modal.body}
                confirmText={modal.confirmText}
                cancelText="Cancel"
                handleCancel={() => setModal(null)}
                handleConfirm={modal.handleConfirm}
                loading={loading}
              />
            )}
          </>
        )}
      </PermissionFilter>
    </Flex>
  );
};

export default Sitemap;